import { ComponentChildren } from 'preact';
import { GuideCoursePage } from '@course/components/guide-course-page';
import { PageNavBar, PageNavBody, PageNavLink } from '@course/components/page-nav-bar';
import {
  IcoBan,
  IcoBell,
  IcoCalendar,
  IcoChartBar,
  IcoCheckBadge,
  IcoCheckCircle,
  IcoCog,
  IcoEye,
  IcoRefresh,
  IcoRocketLaunch,
  IcoTag,
  IcoUserGroup,
  IcoUsers,
} from '@components/icons';
import { URLS } from 'shared/urls';
import { AccessFormatForm } from '@course/components/access-format-radios';
import { FullCourse } from 'server/types';
import { BtnPrimary } from '@components/buttons';
import { NotificationSettings } from './notification-settings';
import { SetPasswordEmailSettings } from './set-password-email-settings';
import { FacilitatorsSettings } from './faciliator-settings';
import { LimitSettings } from './limit-settings';
import { useCurrentTenant, useCurrentUser } from '@components/router/session-context';
import { rpx } from 'client/lib/rpx-client';
import { HeadingSecondary } from '@course/components/headings';
import { CoursePrivacySettingsForm } from '@course/components/course-privacy-settings';
import { useState } from 'preact/hooks';
import { partialStateUpdater } from 'client/lib/hooks';
import { TranscriptSettings } from './transcript-settings';
import { IntegrationSettings } from './integration-settings';
import { CalendarInviteSettings } from './calendar-invite-settings';
import { IcoClosedCaptions } from '@components/icons/video-player';
import { LoadedProps, RouteLoadProps, useRouteParams } from '@components/router';
import { defCoursesRoute } from '@course/components/courses-app-router';
import { CourseProfileFieldsForm } from '@course/components/manage-course-profile-fields';
import { Subtext } from '@components/async-form';
import { CustomCheckoutSettings } from './custom-checkout-settings';
import { CompletionGateSettings } from './completion-gate-settings';
import { CheckinSettings } from './checkin-settings';
import { TrackerSettings } from './tracker-settings';
import { ImmersionSprintSetup } from './immersion-sprint-setup';
import { AcesSettings } from './aces-settings';

type SettingTypes =
  | 'privacy'
  | 'limits'
  | 'notifications'
  | 'courseType'
  | 'facilitators'
  | 'captions'
  | 'profile-fields'
  | 'integrations'
  | 'calendar-invites'
  | 'immersion-sprint'
  | 'completion-gate'
  | 'checkins'
  | 'tracker'
  | 'aces'
  | 'checkout';

export const route = defCoursesRoute({
  load,
  Page,
  authLevel: 'guide',
});

function NavbarItem({
  title,
  description,
  type,
  icon,
}: {
  type: SettingTypes;
  title: string;
  description: string;
  icon: ComponentChildren;
}) {
  const { courseId, settingsType: currentType = 'courseType' } = useRouteParams();

  return (
    <PageNavLink
      isSelected={type === currentType}
      href={URLS.guide.coursePage({
        courseId,
        page: `settings/${type}`,
      })}
    >
      <span class="flex py-2 gap-3">
        <span class="pt-1">{icon}</span>
        <span class="flex flex-col">
          <strong class="capitalize">{title}</strong>
          <span class="opacity-75 font-normal text-xs">{description}</span>
        </span>
      </span>
    </PageNavLink>
  );
}

function CourseTypeSettings({
  course,
  onUpdate,
}: {
  course: FullCourse;
  onUpdate: (accessFormat: FullCourse['accessFormat']) => void;
}) {
  const { terminology } = useCurrentTenant();
  return (
    <div class="flex flex-col gap-6">
      <HeadingSecondary>Change {terminology.course} type</HeadingSecondary>
      <AccessFormatForm
        courseId={course.id}
        accessFormat={course.accessFormat}
        isAbsoluteSchedule={!!course.isAbsoluteSchedule}
        onSuccess={onUpdate}
      >
        <footer>
          <BtnPrimary>Save</BtnPrimary>
        </footer>
      </AccessFormatForm>
    </div>
  );
}

function CourseProfileFieldsSettings({ course }: { course: FullCourse }) {
  return (
    <div class="flex flex-col w-full max-w-7xl mx-auto gap-6">
      <div>
        <HeadingSecondary>Profile fields</HeadingSecondary>
        <Subtext>Select custom profile fields that your students can fill out.</Subtext>
      </div>
      <CourseProfileFieldsForm courseId={course.id} />
    </div>
  );
}

/** Courses run as an immersion sprint: standups, the gate or the tracker on. */
function showsImmersionSprintSetup(course: FullCourse) {
  return (
    !!(course.checkinsEnabled || course.certGateEnabled || course.trackerEnabled) &&
    !course.isBundle &&
    !course.isProduct
  );
}

function CourseSettings({
  course,
  updateCourse,
  currentType,
  captionsAvailable,
}: {
  course: FullCourse;
  updateCourse: (update: Partial<FullCourse>) => void;
  currentType: SettingTypes;
  captionsAvailable: boolean;
}) {
  const tenant = useCurrentTenant();
  const { terminology } = tenant;

  return (
    <GuideCoursePage course={course} type="settings">
      <PageNavBar title="Settings">
        <PageNavBody class="gap-1">
          {!course.isBundle && (
            <NavbarItem
              type="courseType"
              title={`${terminology.course} Type`}
              description={`Change your ${terminology.course} type`}
              icon={<IcoRefresh />}
            />
          )}
          <NavbarItem
            type="limits"
            title="Limits"
            description={`Modify limits for your ${terminology.course}`}
            icon={<IcoBan />}
          />
          {!course.isBundle && (
            <NavbarItem
              type="privacy"
              title="Privacy & Community"
              description={`Manage privacy & community preferences of ${terminology.course}`}
              icon={<IcoEye />}
            />
          )}
          {!course.isBundle && (
            <NavbarItem
              type="notifications"
              title="Notifications"
              description={`Manage which notifications you receive for this ${terminology.course}`}
              icon={<IcoBell />}
            />
          )}
          {!course.isBundle && course.level === 'guide' && (
            <NavbarItem
              type="facilitators"
              title={terminology.Facilitators}
              description={`Configure who can help you manage this ${terminology.course}`}
              icon={<IcoUsers />}
            />
          )}
          {!course.isBundle && captionsAvailable && (
            <NavbarItem
              type="captions"
              title="Captions & Transcripts"
              description={`Manage captions & transcripts preferences for this ${terminology.course}`}
              icon={<IcoClosedCaptions class="w-4 h-4" />}
            />
          )}
          {course.isBundle && captionsAvailable && (
            <NavbarItem
              type="profile-fields"
              title="Profile Fields"
              description={`Select which profile fields you'd like your customers to fill out.`}
              icon={<IcoEye />}
            />
          )}
          {tenant.ontraportAccountId && (
            <NavbarItem
              type="integrations"
              title="Integrations"
              description="Ontraport product ID and purchase tags"
              icon={<IcoCog />}
            />
          )}
          {!course.isBundle && (
            <NavbarItem
              type="calendar-invites"
              title="Calendar Invites"
              description="Auto-send calendar invites to members on enrollment + meeting save"
              icon={<IcoCalendar />}
            />
          )}
          {showsImmersionSprintSetup(course) && (
            <NavbarItem
              type="immersion-sprint"
              title="Immersion sprint"
              description="Launch checklist, week plan and next cohort"
              icon={<IcoRocketLaunch />}
            />
          )}
          {!course.isBundle && (
            <NavbarItem
              type="completion-gate"
              title="Completion Gate"
              description="Weekly lesson-completion deadline, reminders, and optional removal"
              icon={<IcoCheckBadge />}
            />
          )}
          {!course.isBundle && !course.isProduct && (
            <NavbarItem
              type="checkins"
              title="Standups"
              description="Daily student standup prompts, reminders, and email routing"
              icon={<IcoCheckCircle />}
            />
          )}
          {!course.isBundle && !course.isProduct && (
            <NavbarItem
              type="tracker"
              title="Tracker"
              description="Immersion sprint stages and scoreboard"
              icon={<IcoChartBar />}
            />
          )}
          {tenant.isCore && !course.isBundle && !course.isProduct && (
            <NavbarItem
              type="aces"
              title="ACES"
              description="Which immersion sprint ACES shows this course as"
              icon={<IcoUserGroup />}
            />
          )}
          <NavbarItem
            type="checkout"
            title="Checkout Page"
            description="Custom checkout page design"
            icon={<IcoTag />}
          />
        </PageNavBody>
      </PageNavBar>
      <div class="admin-area grow p-8 sm:px-10  max-w-4xl">
        {currentType === 'courseType' && (
          <CourseTypeSettings
            course={course}
            onUpdate={(accessFormat) =>
              updateCourse({
                accessFormat,
              })
            }
          />
        )}
        {currentType === 'limits' && <LimitSettings course={course} onUpdate={updateCourse} />}
        {currentType === 'notifications' && (
          <>
            <NotificationSettings course={course} />
            <SetPasswordEmailSettings course={course} />
          </>
        )}
        {currentType === 'privacy' && (
          <CoursePrivacySettingsForm course={course} onUpdate={updateCourse} />
        )}
        {currentType === 'facilitators' && course.level === 'guide' && (
          <FacilitatorsSettings course={course} />
        )}
        {currentType === 'captions' && captionsAvailable && <TranscriptSettings course={course} />}
        {currentType === 'profile-fields' && <CourseProfileFieldsSettings course={course} />}
        {currentType === 'integrations' && <IntegrationSettings course={course} />}
        {currentType === 'calendar-invites' && !course.isBundle && (
          <CalendarInviteSettings course={course} />
        )}
        {currentType === 'immersion-sprint' && showsImmersionSprintSetup(course) && (
          <ImmersionSprintSetup course={course} />
        )}
        {currentType === 'completion-gate' && !course.isBundle && (
          <CompletionGateSettings course={course} onUpdate={updateCourse} />
        )}
        {currentType === 'checkins' && !course.isBundle && !course.isProduct && (
          <CheckinSettings course={course} onUpdate={updateCourse} />
        )}
        {currentType === 'tracker' && !course.isBundle && !course.isProduct && (
          <TrackerSettings course={course} onUpdate={updateCourse} />
        )}
        {currentType === 'aces' && tenant.isCore && !course.isBundle && !course.isProduct && (
          <AcesSettings course={course} onUpdate={updateCourse} />
        )}
        {currentType === 'checkout' && <CustomCheckoutSettings course={course} />}
      </div>
    </GuideCoursePage>
  );
}

const getDefaultSettingsType = (course: FullCourse): SettingTypes => {
  return course.isBundle ? 'limits' : 'courseType';
};

function Page(props: LoadedProps<typeof load>) {
  const tenant = useCurrentTenant();
  const user = useCurrentUser();
  const [course, setCourse] = useState(props.state.course);
  const currentType = (props.params.settingsType || getDefaultSettingsType(course)) as SettingTypes;
  const captionsAvailable = !tenant.isCore || user?.tier === 'pro';
  const updateCourse = partialStateUpdater(setCourse);

  return (
    <CourseSettings
      course={course}
      currentType={currentType}
      captionsAvailable={captionsAvailable}
      updateCourse={updateCourse}
    />
  );
}

async function load(route: RouteLoadProps) {
  const courseId = route.params.courseId;
  const course = await rpx.courses.getGuideCourse({ id: courseId });

  return {
    course,
  };
}
